import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../api/subscription';

export function SubscriptionBanner() {
  const navigate = useNavigate();
  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: subscriptionApi.get,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!sub) return null;

  // Expired trial
  if (sub.isExpired && sub.plan === 'TRIAL') {
    return (
      <div style={{
        background: 'var(--banner-red-bg)',
        borderBottom: '2px solid #ef4444',
        padding: '0.6rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontSize: '1rem' }}>🔒</span>
          <span style={{ fontWeight: 700, color: 'var(--banner-red-text)', fontSize: '0.875rem' }}>
            Your 7-day free trial has expired.
          </span>
          <span style={{ color: 'var(--banner-red-text-soft)', fontSize: '0.8rem' }}>
            Upgrade to continue creating invoices.
          </span>
        </div>
        <button
          onClick={() => navigate('/subscription')}
          style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, padding: '0.35rem 1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          Upgrade Now
        </button>
      </div>
    );
  }

  // Expired paid plan
  if (sub.isExpired && sub.plan !== 'TRIAL') {
    return (
      <div style={{
        background: 'var(--banner-orange-bg)',
        borderBottom: '2px solid #f97316',
        padding: '0.6rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span>⚠️</span>
          <span style={{ fontWeight: 700, color: 'var(--banner-orange-text)', fontSize: '0.875rem' }}>
            Your {sub.planName} has expired.
          </span>
          <span style={{ color: 'var(--banner-orange-text-soft)', fontSize: '0.8rem' }}>
            Renew to restore full access.
          </span>
        </div>
        <button
          onClick={() => navigate('/subscription')}
          style={{ background: '#f97316', color: 'white', border: 'none', borderRadius: 6, padding: '0.35rem 1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          Renew Now
        </button>
      </div>
    );
  }

  // Trial active — show countdown
  if (sub.plan === 'TRIAL' && sub.trialDaysLeft !== null) {
    const daysLeft = sub.trialDaysLeft;
    const isUrgent = daysLeft <= 2;
    return (
      <div style={{
        background: isUrgent ? 'var(--banner-orange-bg)' : 'var(--primary-light)',
        borderBottom: `2px solid ${isUrgent ? '#f97316' : '#3b82f6'}`,
        padding: '0.5rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span>{isUrgent ? '⏰' : 'ℹ️'}</span>
          <span style={{ fontWeight: 600, color: isUrgent ? 'var(--banner-orange-text)' : 'var(--primary)', fontSize: '0.83rem' }}>
            Free Trial — {daysLeft === 0 ? 'Expires today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
            · {sub.usage.invoices} / 20 invoices used
          </span>
        </div>
        <button
          onClick={() => navigate('/subscription')}
          style={{ background: isUrgent ? '#f97316' : '#3b82f6', color: 'white', border: 'none', borderRadius: 6, padding: '0.3rem 0.85rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          View Plans
        </button>
      </div>
    );
  }

  // Invoice limit warning (any plan)
  if (!sub.withinLimits.invoices && sub.limits.invoicesPerMonth !== null) {
    return (
      <div style={{
        background: 'var(--banner-yellow-bg)',
        borderBottom: '2px solid #eab308',
        padding: '0.5rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
      }}>
        <span style={{ fontSize: '0.83rem', color: 'var(--banner-yellow-text)', fontWeight: 600 }}>
          ⚠️ Invoice limit reached ({sub.usage.invoices}/{sub.limits.invoicesPerMonth}). Upgrade to create more.
        </span>
        <button onClick={() => navigate('/subscription')}
          style={{ background: '#eab308', color: 'white', border: 'none', borderRadius: 6, padding: '0.3rem 0.85rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem' }}>
          Upgrade
        </button>
      </div>
    );
  }

  return null;
}
